import React, { useMemo, useState } from "react";
import { Mafs, Polygon, Line, Text, Theme, Coordinates, Plot } from "mafs";
import { Select, Slider, Switch, Button, Card } from "antd";

const { Option } = Select;

const upper = (v, fallback) => String(v ?? fallback).toUpperCase();
const num = (v, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
};

const is2D = (shapeType) => shapeType === "RECTANGLE" || shapeType === "TRIANGLE";
const is3D = (shapeType) => shapeType === "RECTANGULAR_PRISM" || shapeType === "CYLINDER";

export const AreaVolumeExplorerWidget = ({ config = {}, variables = {} }) => {
    // Align with AreaVolumeConfig + AreaVolumeValidator:
    // width,height,depth are the canonical fields.
    // CYLINDER: width = diameter (validator uses r = w/2).
    const [shapeType, setShapeType] = useState(
        upper(variables.shape, config.shape ?? "RECTANGULAR_PRISM")
    );
    const [measurementType, setMeasurementType] = useState(
        upper(variables.measurementType, config.measurementType ?? "VOLUME")
    );
    const [unit] = useState(variables.unit ?? config.unit ?? "cm");

    const [dims, setDims] = useState({
        width: num(variables.width ?? config.width, 4),
        height: num(variables.height ?? config.height, 3),
        depth: num(variables.depth ?? config.depth, 2),
    });

    const [showLabels, setShowLabels] = useState(true);

    // 30-degree isometric projection for 3D meshes
    const project = ([x, y, z]) => {
        const isoX = (x - z) * Math.cos(Math.PI / 6);
        const isoY = y - (x + z) * Math.sin(Math.PI / 6);
        return [isoX, isoY];
    };

    // Keep measurementType consistent with validator:
    // RECTANGLE/TRIANGLE only support AREA.
    const onShapeChange = (nextShape) => {
        const next = String(nextShape).toUpperCase();
        setShapeType(next);
        if (is2D(next) && measurementType === "VOLUME") {
            setMeasurementType("AREA");
        }
    };

    const results = useMemo(() => {
        const w = num(dims.width, 0);
        const h = num(dims.height, 0);
        const d = num(dims.depth, 0);

        const isArea = measurementType === "AREA";

        let value = 0;
        let formula = "";
        let label = isArea ? (is2D(shapeType) ? "Area" : "Surface Area") : "Volume";
        let unitLabel = isArea ? `${unit}²` : `${unit}³`;

        switch (shapeType) {
            case "RECTANGLE": {
                // Validator: AREA -> w*h, else 0
                value = isArea ? w * h : 0;
                formula = isArea ? `A = w × h = ${w} × ${h}` : `Volume not defined for a 2D shape`;
                break;
            }
            case "TRIANGLE": {
                // Validator: AREA -> 0.5*w*h, else 0
                value = isArea ? 0.5 * w * h : 0;
                formula = isArea ? `A = ½wh = ½ × ${w} × ${h}` : `Volume not defined for a 2D shape`;
                break;
            }
            case "RECTANGULAR_PRISM": {
                // Validator: AREA -> 2(w*h + w*d + h*d), else w*h*d
                if (isArea) {
                    value = 2 * (w * h + w * d + h * d);
                    formula = `SA = 2(wh + wd + hd) = 2(${w}×${h} + ${w}×${d} + ${h}×${d})`;
                } else {
                    value = w * h * d;
                    formula = `V = w × h × d = ${w} × ${h} × ${d}`;
                }
                break;
            }
            case "CYLINDER": {
                // Validator: r = w/2 where w is diameter
                const r = w / 2;
                if (isArea) {
                    value = 2 * Math.PI * r * h + 2 * Math.PI * r * r;
                    formula = `SA = 2πrh + 2πr² (r=w/2)`;
                } else {
                    value = Math.PI * r * r * h;
                    formula = `V = πr²h (r=w/2)`;
                }
                break;
            }
            default:
                value = 0;
                formula = "";
        }

        return { value, formula, unitLabel, label };
    }, [shapeType, measurementType, dims, unit]);

    return (
        <div className="flexColumn gap-medium ch-width-100 animate-fade-in">
            <div className="ch-emph-block bg-slate-50 border rounded-xl overflow-hidden relative">
                <div className="absolute top-4 left-4 z-10">
                    <div className="ch-text-bold text-muted uppercase tracking-wider text-xs">
                        {shapeType.replaceAll("_", " ")} Explorer
                    </div>
                </div>

                <Mafs viewBox={{ x: [-7, 7], y: [-5, 5] }} pan={false} height={380}>
                    <Coordinates.Cartesian visible={false} />

                    {shapeType === "RECTANGLE" && (
                        <RectangleMesh dims={dims} showLabels={showLabels} color={Theme.blue} />
                    )}
                    {shapeType === "TRIANGLE" && (
                        <TriangleMesh dims={dims} showLabels={showLabels} color={Theme.blue} />
                    )}
                    {shapeType === "RECTANGULAR_PRISM" && (
                        <PrismMesh dims={dims} project={project} showLabels={showLabels} color={Theme.blue} />
                    )}
                    {shapeType === "CYLINDER" && (
                        <CylinderMesh dims={dims} project={project} showLabels={showLabels} color={Theme.green ?? Theme.blue} />
                    )}
                </Mafs>

                <div className="text-center padding-y--m border-t bg-white/90 backdrop-blur-sm">
                    <div className="ch-text-small text-muted mb-1">{results.label}</div>
                    <div className="ch-text-xl font-bold text-slate-800">
                        {results.value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        <span className="ml-1 text-slate-500 font-normal">{results.unitLabel}</span>
                    </div>
                    <div className="ch-text-small font-mono text-blue-600 mt-1 px-4 italic">
                        {results.formula}
                    </div>
                </div>
            </div>

            <Card className="shadow-sm border-slate-200">
                <div className="flexColumn gap-medium">
                    <div className="flexRow gap-small">
                        <Select className="flex2" value={shapeType} onChange={onShapeChange}>
                            <Option value="RECTANGLE">Rectangle</Option>
                            <Option value="TRIANGLE">Triangle</Option>
                            <Option value="RECTANGULAR_PRISM">Rectangular Prism</Option>
                            <Option value="CYLINDER">Cylinder</Option>
                        </Select>

                        <Select
                            className="flex1"
                            value={measurementType}
                            onChange={setMeasurementType}
                            disabled={is2D(shapeType)} // validator says VOLUME -> 0 for 2D; keep UI aligned
                        >
                            <Option value="VOLUME" disabled={is2D(shapeType)}>Volume</Option>
                            <Option value="AREA">Surface/Area</Option>
                        </Select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                        {shapeType === "RECTANGLE" || shapeType === "TRIANGLE" ? (
                            <>
                                <DimensionSlider
                                    label="Width (w)"
                                    val={dims.width}
                                    min={1}
                                    max={8}
                                    onChange={(v) => setDims((prev) => ({ ...prev, width: v }))}
                                    unit={unit}
                                />
                                <DimensionSlider
                                    label="Height (h)"
                                    val={dims.height}
                                    min={1}
                                    max={8}
                                    onChange={(v) => setDims((prev) => ({ ...prev, height: v }))}
                                    unit={unit}
                                />
                            </>
                        ) : shapeType === "RECTANGULAR_PRISM" ? (
                            <>
                                <DimensionSlider
                                    label="Width (w)"
                                    val={dims.width}
                                    min={1}
                                    max={8}
                                    onChange={(v) => setDims((prev) => ({ ...prev, width: v }))}
                                    unit={unit}
                                />
                                <DimensionSlider
                                    label="Height (h)"
                                    val={dims.height}
                                    min={1}
                                    max={8}
                                    onChange={(v) => setDims((prev) => ({ ...prev, height: v }))}
                                    unit={unit}
                                />
                                <DimensionSlider
                                    label="Depth (d)"
                                    val={dims.depth}
                                    min={1}
                                    max={8}
                                    onChange={(v) => setDims((prev) => ({ ...prev, depth: v }))}
                                    unit={unit}
                                />
                            </>
                        ) : (
                            <>
                                <DimensionSlider
                                    label="Diameter (w)"
                                    val={dims.width}
                                    min={1}
                                    max={8}
                                    onChange={(v) => setDims((prev) => ({ ...prev, width: v }))}
                                    unit={unit}
                                />
                                <DimensionSlider
                                    label="Height (h)"
                                    val={dims.height}
                                    min={1}
                                    max={8}
                                    onChange={(v) => setDims((prev) => ({ ...prev, height: v }))}
                                    unit={unit}
                                />
                            </>
                        )}
                    </div>

                    <div className="flexSpaceBetween border-t pt-4">
                        <div className="flexAlignCentre gap-small">
                            <Switch size="small" checked={showLabels} onChange={setShowLabels} />
                            <span className="ch-text-small text-muted">Show Dimensions</span>
                        </div>
                        <Button
                            size="small"
                            type="link"
                            onClick={() => setDims({ width: 4, height: 3, depth: 2 })}
                        >
                            Reset View
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
};

const DimensionSlider = ({ label, val, min, max, onChange, unit }) => (
    <div className="py-2">
        <div className="flexSpaceBetween ch-text-small font-semibold text-slate-600 mb-1">
            <span>{label}</span>
            <span>{val} {unit}</span>
        </div>
        <Slider min={min} max={max} step={0.5} value={val} onChange={onChange} />
    </div>
);

// --- 2D SHAPES ---

const RectangleMesh = ({ dims, showLabels, color }) => {
    const w = dims.width;
    const h = dims.height;
    const p = [
        [-w / 2, -h / 2],
        [ w / 2, -h / 2],
        [ w / 2,  h / 2],
        [-w / 2,  h / 2],
    ];

    return (
        <g>
            <Polygon points={p} color={color} fillOpacity={0.15} strokeOpacity={0.5} />
            {showLabels && (
                <>
                    <Text x={0} y={-h / 2 - 0.6} size={14}>w={w}</Text>
                    <Text x={-w / 2 - 0.8} y={0} size={14}>h={h}</Text>
                </>
            )}
        </g>
    );
};

const TriangleMesh = ({ dims, showLabels, color }) => {
    // Right triangle matching A = 1/2 * w * h
    const w = dims.width;
    const h = dims.height;

    const p0 = [-w / 2, -h / 2];
    const p1 = [ w / 2, -h / 2];
    const p2 = [-w / 2,  h / 2];

    return (
        <g>
            <Polygon points={[p0, p1, p2]} color={color} fillOpacity={0.15} strokeOpacity={0.5} />
            {showLabels && (
                <>
                    <Text x={0} y={-h / 2 - 0.6} size={14}>w={w}</Text>
                    <Text x={-w / 2 - 0.8} y={0} size={14}>h={h}</Text>
                </>
            )}
        </g>
    );
};

// --- 3D SHAPES ---

const PrismMesh = ({ dims, project, showLabels, color }) => {
    const w = dims.width;
    const h = dims.height;
    const d = dims.depth;

    const cx = -w / 2, cy = -h / 2, cz = -d / 2;

    const v = [
        [cx,     cy,     cz],
        [cx + w, cy,     cz],
        [cx + w, cy + h, cz],
        [cx,     cy + h, cz],
        [cx,     cy,     cz + d],
        [cx + w, cy,     cz + d],
        [cx + w, cy + h, cz + d],
        [cx,     cy + h, cz + d],
    ].map(project);

    return (
        <g>
            <Polygon points={[v[0], v[1], v[2], v[3]]} color={color} fillOpacity={0.1} strokeOpacity={0.3} />
            <Polygon points={[v[0], v[1], v[5], v[4]]} color={color} fillOpacity={0.1} strokeOpacity={0.3} />
            <Polygon points={[v[0], v[3], v[7], v[4]]} color={color} fillOpacity={0.1} strokeOpacity={0.3} />

            <Polygon points={[v[4], v[5], v[6], v[7]]} color={color} fillOpacity={0.15} />
            <Polygon points={[v[1], v[5], v[6], v[2]]} color={color} fillOpacity={0.15} />
            <Polygon points={[v[3], v[2], v[6], v[7]]} color={color} fillOpacity={0.15} />

            {showLabels && (
                <>
                    <Text x={(v[4][0] + v[5][0]) / 2} y={v[4][1] - 0.5} size={14}>w={w}</Text>
                    <Text x={v[5][0] + 0.4} y={v[5][1] + 0.2} size={14}>d={d}</Text>
                    <Text x={v[4][0] - 0.6} y={(v[4][1] + v[7][1]) / 2} size={14}>h={h}</Text>
                </>
            )}
        </g>
    );
};

const CylinderMesh = ({ dims, project, showLabels, color }) => {
    const diameter = dims.width;
    const r = diameter / 2;
    const h = dims.height;

    const segments = 32;
    const topPoints = [];
    const botPoints = [];

    for (let i = 0; i < segments; i++) {
        const theta = (i / segments) * 2 * Math.PI;
        topPoints.push(project([r * Math.cos(theta),  h / 2, r * Math.sin(theta)]));
        botPoints.push(project([r * Math.cos(theta), -h / 2, r * Math.sin(theta)]));
    }

    const q1 = Math.floor(segments / 4);
    const q3 = Math.floor((3 * segments) / 4);

    return (
        <g>
            <Polygon points={botPoints} color={color} fillOpacity={0.1} />
            <Line.Segment point1={topPoints[q1]} point2={botPoints[q1]} color={color} opacity={0.5} />
            <Line.Segment point1={topPoints[q3]} point2={botPoints[q3]} color={color} opacity={0.5} />
            <Polygon points={topPoints} color={color} fillOpacity={0.2} strokeWidth={2} />

            {showLabels && (
                <>
                    <Line.Segment point1={project([0, h / 2, 0])} point2={project([r, h / 2, 0])} color={Theme.red} />
                    <Text x={project([r / 2, h / 2, 0])[0]} y={project([r / 2, h / 2, 0])[1] + 0.4} size={14} color={Theme.red}>
                        r={r}
                    </Text>
                    <Text x={topPoints[q3][0] - 0.8} y={0} size={14}>h={h}</Text>
                </>
            )}
        </g>
    );
};
